/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Container,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import Confetti from "react-confetti";
import { InactivityHandler } from "@components/InactivityHandler";
import IconSpeed from "@components/Images/IconSpeed";
import { setLoanStatus } from "@store/slices/registerSlice";
import { getProductBaseRoute } from "@helpers/funciones";

const SECONDS_TO_FETCH = 10 * 1000;
const MINUTES_TO_NOTIFY = 30 * 60 * 1000;

interface WaitingRoomProps {
  type: "WaitingRoom" | "WaitingRoomRevision" | "WaitingRoomRevisionCC";
}

const DefaultWaitingRoom = (props: WaitingRoomProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const baseRoute = getProductBaseRoute(pathname);

  const application = useSelector((state: RootState) => state.register.application);
  const [getLoadApplication] = useLazyGetLoanApplicationByIdQuery();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [showNotification, setShowNotification] = useState<boolean>(false);

  const logOut = async () => {
    await signOut();
    navigate("/login");
  };

  useEffect(() => {
    const intervalFetch = setInterval(async () => {
      try {
        const response = await getLoadApplication(application.id).unwrap();
        const url_actual = response?.status_catalog?.url_actual || response?.current_status?.url|| "";
        const code_actual = response?.status_catalog?.code || response?.current_status?.code || "";

        if (url_actual && code_actual) {
          dispatch(setLoanStatus(code_actual));

          // Normaliza por si el backend llegara a mandar un path relativo.
          const targetUrl = url_actual.startsWith("/") ? url_actual : `${baseRoute}${url_actual.replace(/^\/+/, "")}`;

          // Si ya estamos en la misma ruta, no navegamos (evita side-effects innecesarios).
          const currentUrl = `${pathname}${search}`;
          if (targetUrl !== currentUrl) {
            navigate(targetUrl, { replace: true });
          }
        }
      } catch (error) {
        console.error("Ha ocurrido un error", error);
      }
    }, SECONDS_TO_FETCH);

    return () => {
      intervalFetch && clearInterval(intervalFetch);
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    if (props.type === "WaitingRoom") {
      const timer = setTimeout(() => {
        setShowNotification(true);
      }, MINUTES_TO_NOTIFY);

      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <>
      <Grid container sx={{ position: "relative" }} height="90%">
        <InactivityHandler />
        {props.type === "WaitingRoom" ? (
          <Confetti className="confetti-container" numberOfPieces={2000} tweenDuration={20000} recycle={false} />
        ) : (
          <></>
        )}

        <Grid item xs={12} md={12} mt={1}>
          <Box
            sx={{
              my: 8,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Container sx={{ mb: 2 }}>
              <Typography
                fontSize={"31px"}
                fontWeight={"600"}
                align="center"
                color={theme?.palette?.primary?.dark}
                lineHeight={"39.06px"}
              >
                ¡Concluiste exitosamente tu solicitud!
              </Typography>
            </Container>

            {props.type === "WaitingRoomRevisionCC" && (
              <>
                <Box sx={{ mb: 1 }}>
                  <Typography
                    fontWeight={600}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.main}
                    align="center"
                    width={isMobileOrTablet ? "280px" : "400px"}
                  >
                    Te informamos que no pudimos autorizar tu solicitud con nuestro proceso automatizado, por lo que
                    será necesaria una revisión adicional.
                  </Typography>
                </Box>
              </>
            )}

            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography
                fontWeight={300}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                align="center"
                width={isMobileOrTablet ? "280px" : "400px"}
              >
                {props.type === "WaitingRoom" && <>Estamos procesando tu solicitud, esto puede tomar varios minutos.</>}
                {props.type === "WaitingRoomRevision" && (
                  <>
                    Estamos procesando tus documentos y realizando el análisis de tu solicitud esto puede tomar hasta 24
                    horas hábiles.
                  </>
                )}
                {props.type === "WaitingRoomRevisionCC" && (
                  <>Estamos realizando el análisis de tu solicitud esto puede tomar hasta 24 horas hábiles.</>
                )}
              </Typography>
            </Box>

            <Box sx={{ mt: 2, mb: 2 }}>
              <IconSpeed fill={theme.palette.primary.dark} />
            </Box>

            <Box sx={{ mt: 3, mb: 1 }}>
              <Typography
                fontWeight={"300"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                align="center"
                width={isMobileOrTablet ? "280px" : "400px"}
              >
                ¡Despreocúpate! Puedes salir de esta pantalla, tan pronto este lista tu oferta personalizada podrás
                consultarla aquí mismo y te la enviaremos vía WhatsApp
              </Typography>
            </Box>

            <Box sx={{ mt: 2, mb: 1 }}>
              <Button variant="default" onClick={logOut} sx={{ minWidth: "125px" }}>
                Salir
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={showNotification}
        onClose={() => {
          setShowNotification(false);
        }}
      >
        <DialogContent>
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography
              fontWeight={600}
              variant={isMobileOrTablet ? "body2" : "body1"}
              align="center"
              width={isMobileOrTablet ? "280px" : "400px"}
            >
              Continuamos procesando tu solicitud, sin embargo está tomando un poco más de tiempo del esperado.
            </Typography>

            <Typography
              fontWeight={300}
              variant={isMobileOrTablet ? "body2" : "body1"}
              align="center"
              width={isMobileOrTablet ? "280px" : "400px"}
              mt={4}
            >
              No te preocupes, esto normalmente se debe a intermitencias con alguno de nuestros proveedores de
              información. En menos de 2 horas un ejecutivo se pondrá en contacto contigo para informarte del estatus de
              tu solicitud.
            </Typography>
          </Box>

          <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}>
            <Button variant="default" onClick={() => setShowNotification(false)} sx={{ px: 6, py: 1, mt: 2 }}>
              Aceptar
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DefaultWaitingRoom;
